import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, Search, Plus } from "lucide-react";
import { SupporterCard } from "@/components/SupportersStrip";
import SupporterSubmitDialog from "@/components/SupporterSubmitDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SupportersPage — full /supporters listing.
 * Public read; click tracking via /api/supporters/{id}/click.
 * Two-tab UI: 🐦 Friends of the Phoenix / 🤝 Supporters.
 * Category filter chips + name search.
 */
export default function SupportersPage() {
  const [tab, setTab] = useState("person");
  const [allPeople, setAllPeople] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/supporters?kind=person`),
          axios.get(`${API}/supporters?kind=company`),
        ]);
        if (!cancelled) {
          setAllPeople(a.data?.supporters || []);
          setAllCompanies(b.data?.supporters || []);
        }
      } catch {
        if (!cancelled) {
          setAllPeople([]);
          setAllCompanies([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleClick(s) {
    try {
      const { data } = await axios.post(`${API}/supporters/${s.supporter_id}/click`);
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch {
      // silent
    }
  }

  const base = tab === "person" ? allPeople : allCompanies;
  const categories = Array.from(new Set(base.map((s) => s.category || "Other")));
  const list = base.filter((s) => {
    if (cat !== "all" && (s.category || "Other") !== cat) return false;
    if (q && !`${s.name} ${s.tagline || ""} ${s.note || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="supporters-page">
      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] mb-4">
            <Heart size={14} />
            <span className="font-accent text-xs tracking-[0.3em] uppercase">
              The OmniMuscle Family
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl text-white leading-[0.95] mb-3">
            Friends · <span className="text-[#D4AF37]">Supporters</span>
          </h1>
          <p className="text-sm text-neutral-400 max-w-2xl mx-auto">
            Every brand. Every mentor. Every gym. Every person who lifted, listened, or believed.
            This page is for them.
          </p>
          <button
            type="button"
            onClick={() => setSubmitOpen(true)}
            data-testid="supporters-become-cta"
            className="inline-flex items-center gap-2 mt-6 bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 px-5 transition-all"
          >
            <Plus size={14} /> Become a Supporter
          </button>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-6">
          <button
            type="button"
            onClick={() => { setTab("person"); setCat("all"); }}
            data-testid="supporters-page-tab-person"
            className={
              "px-4 py-2.5 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors " +
              (tab === "person"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-zinc-800 text-neutral-400 hover:border-zinc-600 hover:text-white")
            }
          >
            🐦 Friends of the Phoenix · {allPeople.length}
          </button>
          <button
            type="button"
            onClick={() => { setTab("company"); setCat("all"); }}
            data-testid="supporters-page-tab-company"
            className={
              "px-4 py-2.5 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors " +
              (tab === "company"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-zinc-800 text-neutral-400 hover:border-zinc-600 hover:text-white")
            }
          >
            🤝 Supporters · {allCompanies.length}
          </button>
        </div>

        {/* Search + category chips */}
        {base.length > 0 && (
          <div className="mb-6 space-y-3">
            <div className="relative max-w-md mx-auto">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                data-testid="supporters-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, role, or note…"
                className="w-full pl-9 pr-3 py-2 text-sm bg-[#0F0F0F] border border-zinc-800 focus:border-[#D4AF37] text-white outline-none"
              />
            </div>
            {categories.length > 1 && (
              <div className="flex flex-wrap justify-center gap-2">
                <CatChip active={cat === "all"} onClick={() => setCat("all")} testId="supporters-cat-all">
                  All · {base.length}
                </CatChip>
                {categories.map((c) => {
                  const count = base.filter((s) => (s.category || "Other") === c).length;
                  return (
                    <CatChip
                      key={c}
                      active={cat === c}
                      onClick={() => setCat(c)}
                      testId={`supporters-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {c} · {count}
                    </CatChip>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center text-neutral-500 py-12">Loading…</div>
        ) : list.length === 0 ? (
          <div className="text-center py-16 border border-zinc-900 bg-[#0F0F0F]">
            <div className="text-neutral-500 mb-4">
              {base.length === 0
                ? `No ${tab === "person" ? "friends" : "supporters"} added yet.`
                : "No matches for that filter."}
            </div>
            {base.length === 0 && (
              <button
                type="button"
                onClick={() => setSubmitOpen(true)}
                data-testid="supporters-empty-cta"
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] hover:text-white font-bold"
              >
                <Plus size={12} /> Be the first
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="supporters-page-list">
            {list.map((s) => (
              <SupporterCard key={s.supporter_id} s={s} onClick={() => handleClick(s)} />
            ))}
          </div>
        )}
      </section>

      <SupporterSubmitDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
      />
    </div>
  );
}

function CatChip({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold border transition-colors " +
        (active
          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
          : "border-zinc-800 text-neutral-400 hover:border-zinc-600 hover:text-white")
      }
    >
      {children}
    </button>
  );
}
